/**
 * useAgentRunReviews — fetch the agent evaluation review queue for the
 * "Agent Reviews" list on the admin issue-triage surface.
 *
 * `agent_run_reviews` is deny-all to clients by deliberate design (the
 * narrative rationale can echo run PII the regex scrubbers cannot catch), so
 * this hook reads through the `agentInfra_listRunReviews` callable — the only
 * sanctioned path — never via a client-direct Firestore query. The callable
 * is gated server-side by withModuleAccess("AI_SYSTEMS") + requireOrgAdmin.
 *
 * Paged (#4966): the first page is the newest 200; `fetchNextPage` asks for
 * the next 200 after the last review's runId. The server's newest-200 cap
 * used to hide older reviews entirely. `data` flattens every loaded page into
 * one newest-first list; `total` is the org-wide count.
 *
 * Status changes go through useResolveRunReviews.
 */
import { useInfiniteQuery, type InfiniteData } from "@tanstack/react-query";
import { httpsCallable } from "../../lib/firebase/authReadyCallable";
import { invokeNamedCallable } from "../../lib/telemetry/namedCallable";
import { functions } from "../../config/firebase";
import { useAuth } from "../../contexts/AuthContext";

/** Page size requested from the callable (its server-side max). */
const PAGE_LIMIT = 200;

export interface AgentRunReviewItem {
  runId: string;
  agentConfigId: string;
  verdict: "FLAG" | "FAIL";
  scores: Record<string, number>;
  rationale: string;
  suggestedFix?: string;
  /** Why the run was not a PASS (evaluator); absent on reviews written before #4966. */
  flagReasons?: string[];
  status: string;
  /** ISO-8601 string (serialized server-side) — null if the doc lacks it. */
  createdAt: string | null;
  /** Present once a reviewer resolved it (#4966). */
  resolutionReason?: string;
  resolvedByEmail?: string;
  resolvedAt?: string;
}

/** One page as the callable returns it. */
export interface AgentRunReviewsPage {
  reviews: AgentRunReviewItem[];
  total: number;
  nextCursor?: string | null;
}

/** Every loaded page, flattened for the list. */
export interface AgentRunReviewsResponse {
  reviews: AgentRunReviewItem[];
  total: number;
}

export const OPEN_REVIEW_STATUSES: ReadonlySet<string> = new Set(["new", "reviewing"]);

export const agentRunReviewKeys = {
  all: ["agentRunReviews"] as const,
  lists: () => [...agentRunReviewKeys.all, "list"] as const,
  list: (limit: number) => [...agentRunReviewKeys.lists(), { limit }] as const,
};

/** The exact query key the list uses — shared with the resolve mutation's optimistic update. */
export const AGENT_RUN_REVIEWS_QUERY_KEY = agentRunReviewKeys.list(PAGE_LIMIT);

export type AgentRunReviewsCache = InfiniteData<AgentRunReviewsPage, string | undefined>;

/** Flatten loaded pages; total comes from the newest page the server answered. */
export function flattenReviewPages(data: AgentRunReviewsCache): AgentRunReviewsResponse {
  const pages = data.pages;
  return {
    reviews: pages.flatMap((p) => p.reviews),
    total: pages.length > 0 ? pages[pages.length - 1].total : 0,
  };
}

export function useAgentRunReviews() {
  const { user } = useAuth();

  return useInfiniteQuery({
    queryKey: AGENT_RUN_REVIEWS_QUERY_KEY,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<AgentRunReviewsPage> => {
      const fn = httpsCallable<{ limit: number; cursor?: string }, AgentRunReviewsPage>(
        functions,
        "agentInfra_listRunReviews"
      );
      const payload = pageParam ? { limit: PAGE_LIMIT, cursor: pageParam } : { limit: PAGE_LIMIT };
      // Named so a failure of THIS query is distinguishable on
      // /admin/issue-triage (OFFICEHUB-CLIENT-69) rather than arriving as a
      // bare `FirebaseError: internal`.
      const result = await invokeNamedCallable("agentInfra_listRunReviews", () => fn(payload));
      return result.data;
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    select: flattenReviewPages,
    enabled: !!user,
    staleTime: 30000, // 30 seconds — same cadence as useIssueReports
  });
}
